import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Product, CloudActions } from '@portal/portal-core';
import { currentTabSelector, getSelectedRows, selectAdminCostCenterState } from 'selectors';
import { ProductTabActions, CloudAdminActions } from 'actions';
import { costCenterInfoForSelectedAdminProductTab } from 'utils';
import { useAppDispatch } from 'store/hooks';

const { PRODUCT_MARKETING_NAMES } = Product;

interface TabProps {
  selected?: boolean;
  hidden?: boolean;
}

const Tab = styled.div<TabProps>`
  min-width: fit-content;
  height: ${({ theme }) => theme.grid.units(1.5)};
  margin-left: ${({ theme }) => theme.grid.units(0.25)};
  margin-right: ${({ theme }) => theme.grid.units(0.25)};
  margin-top: ${({ theme }) => theme.grid.units(0.5)};
  padding: ${({ theme }) => theme.grid.units(0.5)};
  background-color: ${(props) => props.theme.colors[props.selected ? 'white' : 'tabInactive']};
  color: ${({ theme }) => theme.colors.selectableDarkBg};
  border-top-right-radius: ${({ theme }) => theme.borderRadius};
  border-top-left-radius: ${({ theme }) => theme.borderRadius};
  display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
  flex: 2 1 auto;
  justify-content: center;
  align-items: center;
  text-wrap: nowrap;
  font-size: ${({ theme }) => theme.fontSizes.h5};
  font-weight: ${(props) => props.theme.fontWeights[props.selected ? 'bold' : 'normal']};
  cursor: pointer;
  outline: none !important;
`;

// Cost centers arrive from the admin API as a map keyed by id; this matches the shape
// costCenterInfoForSelectedAdminProductTab reads.
type CostCenterMap = Record<string, { name: string; id: string; productAccess: Array<{ name: string }> }>;

interface ProductTabProps {
  product: string;
  hidden?: boolean;
}

const ProductTab = ({ product, hidden }: ProductTabProps): React.JSX.Element => {
  const selectedProduct = useSelector(currentTabSelector) as string;
  const rowsSelected = useSelector(getSelectedRows) as unknown[];
  const adminCostCenterState = useSelector(selectAdminCostCenterState) as CostCenterMap;

  const dispatch = useAppDispatch();
  const updateSelectedProduct = (name: string): void => {
    dispatch(ProductTabActions.selectedProductTab(name));
  };
  const clearSelectedRows = (): void => {
    dispatch(CloudAdminActions.clearRowsSelected());
  };
  const selectedCostCenterDispatch = (id: string, productName: string): void => {
    dispatch(CloudActions.userAdminForceUpdate(id, productName));
  };

  const setCostCenter = (costCenters: CostCenterMap, productName: string): void => {
    if (costCenters && Object.keys(costCenters).length > 0) {
      const costCenterObj = costCenterInfoForSelectedAdminProductTab(costCenters, productName);
      if (costCenterObj) {
        selectedCostCenterDispatch(costCenterObj.id, productName);
      }
    }
  };

  const updateSelectedTab = (productName: string): void => {
    if (selectedProduct !== productName) {
      updateSelectedProduct(productName);
      // if there is a list of admin cost centers and there is a cost center with a product license name that matches selectedProduct
      setCostCenter(adminCostCenterState, productName);
      if (rowsSelected.length > 0) {
        clearSelectedRows();
      }
    }
  };

  return (
    <Tab
      selected={selectedProduct === product}
      onClick={() => updateSelectedTab(product)}
      role="button"
      tabIndex={0}
      hidden={hidden}
    >
      {`${PRODUCT_MARKETING_NAMES[product]} Software`}
    </Tab>
  );
};

export default ProductTab;
